<template>
  <v-expansion-panels class="mb-4">
    <v-expansion-panel>
      <v-expansion-panel-header class="d-flex justify-space-between">
        <div>
          <span class="font-weight-medium text-h6">SLA de Primeira Resposta</span>
          <div class="text--secondary text-body-2 mt-1">
            Aplica-se a todos os tickets da fila, independente do tipo de solicitação - inclusive antes de serem tabulados.
          </div>
        </div>
      </v-expansion-panel-header>

      <v-expansion-panel-content>
        <v-row>
          <v-col
            md="4"
            cols="12"
          >
            <v-switch
              v-model="permitirSla"
              label="SLA de Primeira Resposta"
            />
          </v-col>
        </v-row>

        <v-expand-transition>
          <v-row v-if="permitirSla">
            <v-col
              md="6"
              cols="12"
            >
              <v-text-field
                v-model="diasSla"
                label="Dias"
                type="number"
                dense
                outlined
                min="0"
                max="31"
                :prepend-icon="icons.mdiCalendarToday"
                @change="handleSlaChange"
              />
            </v-col>

            <v-col
              md="6"
              cols="12"
            >
              <v-dialog
                ref="slaDialog"
                v-model="modalSlaDialog"
                :return-value.sync="horasMinutosSla"
                persistent
                width="290px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="horasMinutosSla"
                    label="Horas e minutos"
                    :prepend-icon="icons.mdiClockTimeFourOutline"
                    readonly
                    v-bind="attrs"
                    dense
                    outlined
                    v-on="on"
                  />
                </template>

                <v-time-picker
                  v-if="modalSlaDialog"
                  v-model="horasMinutosSla"
                  full-width
                  format="24hr"
                >
                  <v-spacer></v-spacer>

                  <v-btn
                    text
                    color="primary"
                    @click="modalSlaDialog = false"
                  >
                    Cancel
                  </v-btn>

                  <v-btn
                    text
                    color="primary"
                    @click="
                      $refs.slaDialog.save(horasMinutosSla)
                      handleSlaChange()
                    "
                  >
                    OK
                  </v-btn>
                </v-time-picker>
              </v-dialog>
            </v-col>

            <v-col cols="12">
              <sla-configuracao-alerta
                :sla-alertas.sync="alertasLocale"
                :niveis-alerta="niveisAlerta"
                :get-is-alerta-valido="getIsAlertaValido"
              />
            </v-col>
          </v-row>
        </v-expand-transition>

        <v-row class="justify-center mt-5">
          <v-btn
            color="primary"
            :disabled="loading"
            @click="salvar"
          >
            <v-progress-circular
              v-if="loading"
              indeterminate
              color="secondary"
            />
            <span v-else>Salvar</span>
          </v-btn>
        </v-row>
      </v-expansion-panel-content>
    </v-expansion-panel>
  </v-expansion-panels>
</template>

<script>
import Vue from 'vue'
import { mdiCalendarToday, mdiClockTimeFourOutline } from '@mdi/js'
import { ref, watch, onMounted } from '@vue/composition-api'
import SlaConfiguracaoAlerta from './configuracao-alerta/SlaConfiguracaoAlerta.vue'
import FilaService from '@/services/modules/FilaService'

export default {
  components: {
    SlaConfiguracaoAlerta,
  },

  props: {
    filaId: {
      type: Number,
      required: true,
    },
  },

  setup(props) {
    const niveisAlerta = {
      1: 'Azul <= 20%',
      2: 'Verde > 20% e <= 50%',
      3: 'Laranja > 50% e <= 80%',
      4: 'Vermelho > 80%',
    }

    const slaFunctions = {
      dias: totalmins => Math.floor(Math.floor(totalmins / 60) / 24),
      horasMinutos: totalmins => {
        const mins = String(totalmins % 60).padStart(2, '0')
        const hours = String(Math.floor(totalmins / 60) % 24).padStart(2, '0')

        return `${hours}:${mins}`
      },
    }

    function preencherAlertas(alertas) {
      const alertasCopy = JSON.parse(JSON.stringify(alertas || []))
      const alertasKeys = Object.keys(niveisAlerta).map(Number)

      alertasKeys
        .filter(id => !alertasCopy.some(alerta => alerta.tipoAlertaId === id))
        .forEach(id => {
          alertasCopy.push({
            tipoAlertaId: id,
            notificarAgente: false,
            notificarGestor: false,
            notificarViaBrowser: false,
            notificarViaBadge: false,
          })
        })

      alertasCopy.sort((a, b) => a.tipoAlertaId - b.tipoAlertaId)

      return alertasCopy
    }

    function getIsAlertaValido(alerta) {
      const isAtLeastOneNotificationActive = alerta.notificarViaBrowser || alerta.notificarViaBadge
      const isAtLeastOnePersonActive = alerta.notificarAgente || alerta.notificarGestor

      return isAtLeastOneNotificationActive && isAtLeastOnePersonActive
    }

    const loading = ref(false)
    const permitirSla = ref(false)
    const alertasLocale = ref(preencherAlertas([]))
    const diasSla = ref(slaFunctions.dias(0))
    const horasMinutosSla = ref(slaFunctions.horasMinutos(0))
    const valorSla = ref(0)
    const modalSlaDialog = ref(false)

    function handleSlaChange() {
      valorSla.value = diasSla.value * 24 * 60 + horasMinutosSla.value.split(':')[0] * 60 + horasMinutosSla.value.split(':')[1] * 1

      if (valorSla.value > 0 && valorSla.value < 1) {
        Vue.$toast.clear()
        Vue.$toast.warning('A SLA de primeira resposta deve ser de pelo menos 1 minuto.', { timeout: false })
      }
    }

    async function carregar() {
      const dados = await FilaService.getSlaPrimeiraResposta(props.filaId)

      permitirSla.value = dados?.alertas.some(getIsAlertaValido) || false
      alertasLocale.value = preencherAlertas(dados?.alertas)

      diasSla.value = slaFunctions.dias(dados?.tempoSlaEmMinutos || 0)
      horasMinutosSla.value = slaFunctions.horasMinutos(dados?.tempoSlaEmMinutos || 0)
      valorSla.value = dados?.tempoSlaEmMinutos || 0
    }
    onMounted(carregar)
    watch(() => props.filaId, carregar)

    async function salvar() {
      if (permitirSla.value && valorSla.value < 1) {
        Vue.$toast.clear()
        Vue.$toast.error('A SLA de primeira resposta deve ser de pelo menos 1 minuto.')
        return
      }

      const body = {
        tempoSlaEmMinutos: permitirSla.value ? valorSla.value : 0,
        alertas: permitirSla.value ? alertasLocale.value.filter(getIsAlertaValido) : [],
      }

      try {
        loading.value = true
        await FilaService.atualizarSlaPrimeiraResposta(props.filaId, body)
        Vue.$toast.success('SLA de Primeira Resposta atualizado com sucesso.')
      } finally {
        loading.value = false
      }
    }

    return {
      niveisAlerta,
      loading,
      permitirSla,
      alertasLocale,
      diasSla,
      horasMinutosSla,
      modalSlaDialog,
      handleSlaChange,
      getIsAlertaValido,
      salvar,
      icons: {
        mdiCalendarToday,
        mdiClockTimeFourOutline,
      },
    }
  },
}
</script>
